import * as React from 'react';
import { Fabric } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { useBetreuer } from 'api/betreuer';
import { useKundeList } from 'api/kunde';
import { SelectOption } from 'formComponents/Select/types';
import classnames from 'classnames';
import { viewStyles } from 'styles/viewStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Select } from 'formComponents/Select/Select';
import { ValueType } from 'react-select';
import { Lieferung, LieferungFilter, useLieferungList } from 'api/lieferung';
import { LieferungItem } from 'components/Lieferung/LieferungItem';
import useDebouncedState from 'hooks/useDebouncedState';
import { LieferungPosition, LieferungPositionFilter, useLieferungPositionList } from 'api/lieferungPosition';
import { DebouncedSearchBox } from 'formComponents/DebouncedSearchBox';
import { LieferungEdit, LieferungEditData } from 'components/Lieferung/LieferungEdit';
import { List } from '@fluentui/react/lib/List';
import AutoSizer from 'react-virtualized-auto-sizer';
import moment from 'moment-timezone';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import usePersistentState from 'hooks/usePersistentState';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { useHistory, useParams } from 'react-router-dom';
import { RecordViewUrlParams } from 'layout/App/types';
import { createRecordUrl, decodeRecordNr } from 'utils/formatter';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';
import { LieferungPositionenHeader } from 'components/Lieferung/LieferungPositionenHeader';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { LieferungStatusMulti } from 'formComponents/lookups/LieferungStatusMulti';

const defaultMaxRows = 250;

const styles = mergeStyleSets({
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 150px 150px 170px 170px 5fr 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall1: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 150px 180px 5fr 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall2: {
        display: 'grid',
        gridTemplateColumns: '170px 170px',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

interface FilterValues {
    maxRows: number;
    kNr?: number;
    nr?: string;
    auNr?: string;
    status: string;
    statusList: number[];
    betreuer: SelectOption;
    datumVon?: string;
    datumBis?: string;
    lArt: string;
    searchText: string;
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    kNr: undefined,
    nr: undefined,
    auNr: '',
    status: '__all__',
    statusList: [],
    betreuer: alleBetreuerOption,
    datumVon: undefined,
    datumBis: undefined,
    lArt: '__all__',
    searchText: '',
};

export const LieferungView = (): JSX.Element => {
    const auth = useSelector(authSelector);
    const history = useHistory();
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const [viewMode, setViewMode] = usePersistentState<'short' | 'long'>('long', 'LieferungView.viewMode');

    const { urlKundeNr, urlRecordNr } = useParams<RecordViewUrlParams>();
    const kNr = urlKundeNr ? parseInt(urlKundeNr, 10) : undefined;

    const isSmallScreenEmbedded = useMediaQuery({ query: `(max-width: 2100px)` });
    const isSmallScreenNotEmbedded = useMediaQuery({ query: `(max-width: 1750px)` });
    const isSmallScreen = urlKundeNr ? isSmallScreenEmbedded : isSmallScreenNotEmbedded;

    const lieferungEditData = React.useMemo<LieferungEditData | null>(() => {
        if (urlRecordNr) {
            return {
                lNr: decodeRecordNr(urlRecordNr),
                kNr,
            };
        }
        return null;
    }, [kNr, urlRecordNr]);

    const kundeList = useKundeList({ nr: kNr || -1 });

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
        if (auth.rolle === 'AD') {
            return {
                ...defaultFilterValues,
                betreuer: {
                    value: auth.kz || '',
                    label: auth.name || '?',
                },
            };
        }
        return {
            ...defaultFilterValues,
            betreuer: alleBetreuerOption,
        };
    }, 1000);

    const filter = React.useMemo<LieferungFilter>(() => {
        return {
            kNr,
            betreuer:
                debouncedFilterValues.betreuer.value === '__all__'
                    ? undefined
                    : parseInt(debouncedFilterValues.betreuer.value, 10),
            // status: debouncedFilterValues.status === '__all__' ? undefined :
            // parseInt(debouncedFilterValues.status, 10),
            statusList: debouncedFilterValues.statusList,
            nrSearch: debouncedFilterValues.nr,
            auNrSearch: debouncedFilterValues.auNr,
            datumVon: debouncedFilterValues.datumVon,
            datumBis: debouncedFilterValues.datumBis,
            lArt: debouncedFilterValues.lArt === '__all__' ? undefined : debouncedFilterValues.lArt,
            searchText: debouncedFilterValues.searchText,
            withKunde: true,
            maxRows: defaultMaxRows,
        };
    }, [kNr, debouncedFilterValues]);

    const lieferungList = useLieferungList(filter);

    const positionenFilter = React.useMemo<LieferungPositionFilter>(() => {
        if (lieferungList.isLoading || lieferungList.isLoading || lieferungList.isError) {
            return {};
        }

        return {
            lNrList: lieferungList.data?.map((lieferung) => lieferung.nr) || [],
        };
    }, [lieferungList]);

    const lieferungPositionList = useLieferungPositionList(positionenFilter, { enabled: false });

    React.useEffect(() => {
        if (positionenFilter.lNrList) {
            lieferungPositionList.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionenFilter]);

    const lieferungPositionenByANr = React.useMemo<{ [key: string]: LieferungPosition[] }>(() => {
        if (
            lieferungPositionList.isLoading ||
            lieferungPositionList.isLoading ||
            lieferungPositionList.isError ||
            (lieferungPositionList.data?.length || 0) === 0
        ) {
            return {};
        }

        const newLieferungPositionenByANr: { [key: string]: LieferungPosition[] } = {};

        lieferungPositionList.data?.forEach((lieferungPosition) => {
            if (!newLieferungPositionenByANr[lieferungPosition.lNr]) {
                newLieferungPositionenByANr[lieferungPosition.lNr] = [];
            }

            newLieferungPositionenByANr[lieferungPosition.lNr].push(lieferungPosition);
        });

        return newLieferungPositionenByANr;
    }, [lieferungPositionList]);

    const onRenderCell = React.useCallback(
        (lieferung: Lieferung | undefined): JSX.Element => {
            if (!lieferung) {
                return <div />;
            }

            return (
                <LieferungItem
                    key={lieferung.nr}
                    lieferung={lieferung}
                    withKunde={!urlKundeNr}
                    viewMode={viewMode}
                    withHeader={false}
                    lieferungPositionen={lieferungPositionenByANr[lieferung.nr] || []}
                    editLieferung={() => history.push(createRecordUrl('lieferungen', urlKundeNr, lieferung.nr))}
                />
            );
        },
        [lieferungPositionenByANr, urlKundeNr, viewMode, history],
    );

    const renderDatumVon = () => (
        <FormDatePicker
            placeholder="Datum (von)"
            value={filterValues.datumVon}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    const renderDatumBis = () => (
        <FormDatePicker
            placeholder="Datum (bis)"
            value={filterValues.datumBis}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    if (auth.rolle === 'AD' && kundeList.data?.length === 1) {
        if (kundeList.data[0].betreuer && kundeList.data[0].betreuer !== auth.nr) {
            return (
                <Fabric className={viewStyles.container}>
                    <div className={viewStyles.listContainer}>
                        <div className={viewStyles.noData}>Keine Daten.</div>
                    </div>
                </Fabric>
            );
        }
    }

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric
            className={classnames(viewStyles.container, {
                [viewStyles.twoSearchBarLines]: isSmallScreen,
                [viewStyles.embedded]: !!urlKundeNr,
            })}
        >
            <Helmet>
                {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                <title>{lieferungEditData?.lNr ? 'Lieferung ' + lieferungEditData?.lNr : 'Lieferungen'}</title>
            </Helmet>
            <div className={isSmallScreen ? styles.searchBarSmall1 : styles.searchBar}>
                <DebouncedSearchBox
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            searchText: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Lieferung-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            nr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Auftrag-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            auNr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <LieferArt
                    value={filterValues.lArt || ''}
                    addAll
                    menuPlacement="bottom"
                    renderLabel="placeholder"
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            lArt: newValue,
                        });
                    }}
                />
                <Select
                    name="betreuer"
                    value={filterValues.betreuer}
                    options={betreuerFilterOptions}
                    onChange={(newValue: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            betreuer: newValue || alleBetreuerOption,
                        });
                    }}
                />

                {isSmallScreen || renderDatumVon()}
                {isSmallScreen || renderDatumBis()}

                <LieferungStatusMulti
                    value={filterValues.statusList.map((v) => v.toString())}
                    renderLabel="placeholder"
                    onChange={(newValues) => {
                        setFilterValues({
                            ...filterValues,
                            statusList: newValues.map((v) => parseInt(v, 10)),
                        });
                    }}
                />

                <div />

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Kurzansicht"
                        checked={viewMode === 'short'}
                        onChange={(event, checked) => {
                            setViewMode(checked ? 'short' : 'long');
                        }}
                    />
                </div>
            </div>

            {isSmallScreen && (
                <div className={styles.searchBarSmall2}>
                    {renderDatumVon()}
                    {renderDatumBis()}
                </div>
            )}

            {lieferungList.isLoading || lieferungList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <div className={viewStyles.listContainer}>
                    {lieferungList?.data && lieferungList?.data.length === 0 ? (
                        <div className={viewStyles.noData}>Keine Lieferungen.</div>
                    ) : (
                        <>
                            {viewMode === 'short' && (
                                <div className={viewStyles.listContainer}>
                                    <table className={formStyles.stickyPositionenHeaderTable}>
                                        <LieferungPositionenHeader />
                                    </table>
                                </div>
                            )}
                            <AutoSizer>
                                {({ height, width }) => (
                                    <div
                                        style={{ width, height: height - 25, overflowX: 'hidden', overflowY: 'scroll' }}
                                    >
                                        <List
                                            key={viewMode + (lieferungPositionList.isSuccess ? 1 : 0)}
                                            items={lieferungList.data || []}
                                            onRenderCell={onRenderCell}
                                        />
                                    </div>
                                )}
                            </AutoSizer>
                        </>
                    )}
                </div>
            )}
            {lieferungEditData !== null && (
                <LieferungEdit
                    defaultKNr={lieferungEditData.kNr}
                    lNr={lieferungEditData.lNr}
                    onClose={(refetch) => {
                        history.push(createRecordUrl('lieferungen', urlKundeNr));
                        if (refetch) {
                            lieferungList.refetch().then();
                        }
                    }}
                    onReopen={(newLNr: string) => {
                        history.push(createRecordUrl('lieferungen', urlKundeNr));
                        lieferungList.refetch().then(() => {
                            history.push(createRecordUrl('lieferungen', urlKundeNr, newLNr));
                        });
                    }}
                />
            )}
        </Fabric>
    );
};
